// Phase 8C — Second Opinion inline card.
//
// Renders a structured critical analysis of a plan with risk flags,
// blind spots, and alternative suggestions.

import { Icon } from "../../../components/icons/Icon.tsx";

interface RiskFlag {
  severity: string;
  flag: string;
  detail: string;
}

interface SecondOpinionData {
  scenario_reviewed?: string;
  risk_flags?: RiskFlag[];
  blind_spots?: string[];
  alternative_suggestions?: string[];
  verdict?: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  high: "var(--sika-negative, #C0392B)",
  medium: "var(--sika-warning, #B45309)",
  low: "var(--sika-text-muted, #606060)",
};

export function SikaSecondOpinionCard({ data }: { data: unknown }): JSX.Element {
  const d = (data ?? {}) as SecondOpinionData;

  return (
    <div className="plan-card" style={{ borderColor: "var(--sika-border-strong)" }}>
      <div className="plan-head">
        <div className="label">Second Opinion</div>
        <div className="ttl" style={{ fontFamily: "var(--sika-font-sans)", fontSize: "var(--sika-text-lg)" }}>
          Devil&apos;s Advocate Analysis
        </div>
        <div className="meta">
          {d.scenario_reviewed ? `${d.scenario_reviewed} scenario` : "Plan review"}
        </div>
      </div>

      {/* Risk flags */}
      {d.risk_flags && d.risk_flags.length > 0 && (
        <div className="honest-assessment">
          <div className="assess-label">Risk flags</div>
          {d.risk_flags.map((f, i) => (
            <div key={i} className="assess-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
              <span className="lbl" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Icon name="alert" size={12} color={SEVERITY_COLORS[f.severity] ?? SEVERITY_COLORS.low} />
                <strong style={{ color: SEVERITY_COLORS[f.severity] ?? "inherit" }}>
                  {f.severity.toUpperCase()}
                </strong>
                {" — "}{f.flag}
              </span>
              <span className="val" style={{ fontFamily: "var(--sika-font-sans)", fontSize: "var(--sika-text-sm)", color: "var(--sika-text-secondary)" }}>
                {f.detail}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Blind spots */}
      {d.blind_spots && d.blind_spots.length > 0 && (
        <div className="honest-assessment">
          <div className="assess-label">Blind spots</div>
          {d.blind_spots.map((s, i) => (
            <div key={i} className="assess-row">
              <span className="lbl" style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
                <Icon name="eye" size={12} />
                {s}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Alternatives */}
      {d.alternative_suggestions && d.alternative_suggestions.length > 0 && (
        <div className="honest-assessment">
          <div className="assess-label">Alternative approaches</div>
          {d.alternative_suggestions.map((s, i) => (
            <div key={i} className="assess-row">
              <span className="lbl" style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
                <Icon name="bolt" size={12} />
                {s}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Verdict */}
      {d.verdict && (
        <div className="plan-disclosure" style={{ fontStyle: "italic", fontSize: "var(--sika-text-sm)" }}>
          {d.verdict}
        </div>
      )}
    </div>
  );
}
